/**
 * Script Loader Utility
 * 
 * Handles the dynamic loading of the remote SDK implementation script.
 * Provides error handling and prevents duplicate script injection.
 */

import type { EncatchConfig } from './types';

/** Default web host for the remote implementation script */
const DEFAULT_WEB_HOST = 'https://app.encatch.com';

/** Fixed path for the SDK script */
const SDK_SCRIPT_PATH = '/s/sdk/v1/encatch.js';

/** Script element ID to prevent duplicate injections */
const SCRIPT_ID = 'encatch-sdk-script';

/**
 * Returns a cache-bust string that changes at most every 30 minutes (YYMMDDHHmm, mm is 00 or 30).
 * Allows the script to be cached for up to 30 minutes before a new URL triggers a fresh fetch.
 */
function getCacheBustParam(): string {
  const d = new Date();
  const yy = String(d.getUTCFullYear()).slice(-2);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const min30 = Math.floor(d.getUTCMinutes() / 30) * 30;
  const minStr = String(min30).padStart(2, '0');
  return `${yy}${mm}${dd}${hh}${minStr}`;
}

/** Track loading state */
let isLoading = false;
let isLoaded = false;
let loadError: Error | null = null;

/**
 * Load the remote SDK implementation script
 * 
 * @param config - Configuration containing optional custom web host
 * @returns Promise that resolves when script loads, rejects on error
 */
export function loadRemoteScript(config: EncatchConfig): Promise<void> {
  // Return early if already loaded
  if (isLoaded) {
    return Promise.resolve();
  }

  // Return error if previous load failed
  if (loadError) {
    return Promise.reject(loadError);
  }

  // Return existing promise if already loading
  if (isLoading) {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (isLoaded) {
          clearInterval(checkInterval);
          resolve();
        } else if (loadError) {
          clearInterval(checkInterval);
          reject(loadError);
        }
      }, 50);
    });
  }

  isLoading = true;

  return new Promise((resolve, reject) => {
    // Check if script already exists in DOM
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      isLoading = false;
      isLoaded = true;
      resolve();
      return;
    }

    // Determine script URL from webHost (with 30-min cache-bust so script is cached at most 30 min)
    const webHost = config.webHost || DEFAULT_WEB_HOST;
    const scriptUrl = webHost + SDK_SCRIPT_PATH + '?dt=' + getCacheBustParam();

    // Create and configure script element
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = scriptUrl;
    script.type = 'module';
    script.async = true;

    // Handle successful load
    script.onload = () => {
      isLoading = false;
      isLoaded = true;
      resolve();
    };

    // Handle load error
    script.onerror = (event) => {
      isLoading = false;
      loadError = new Error(
        `[Encatch] Failed to load SDK from ${scriptUrl}. ` +
        `Please check your network connection and web host configuration.`
      );
      console.error(loadError.message, event);
      reject(loadError);
    };

    // Inject script into document head
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });
}

/**
 * Check if the remote script has been loaded
 */
export function isScriptLoaded(): boolean {
  return isLoaded;
}

/**
 * Reset loader state (useful for testing)
 */
export function resetLoaderState(): void {
  isLoading = false;
  isLoaded = false;
  loadError = null;
}

/**
 * Get the default script URL (includes current 30-min cache-bust param)
 */
export function getDefaultScriptUrl(): string {
  return DEFAULT_WEB_HOST + SDK_SCRIPT_PATH + '?dt=' + getCacheBustParam();
}
